import {Box, Button, Typography, useTheme} from "@mui/material"
import {abcd}                                                   from "../../util/prepkit-utils"
import {ExpandLess, ExpandMore, LockOpen}                       from "@mui/icons-material"
import React, {useState}                                       from "react"
import LeftRight                                                from "../LeftRight"
import QuestionTranslator                                       from "../QuestionTranslator"
import useMediaQuery                                            from "@mui/material/useMediaQuery"
import {useNavigate}                                            from "react-router-dom"

// `revealCorrect` is owned by QuestionView — the "Show correct answer" control lives in the
// Ask Birdsy action buttons (desktop) / the action sheet (small screens), not on this card.
//
// `onChangeAnswer` (101): when supplied, an INCORRECT answer offers a Change Answer link. Only
// incorrect — nobody wants to change an answer they got right, and offering it there would only
// invite second-guessing. The unlocking itself is QuestionView's job; this renders the affordance.
const Question = ({prepKit, question, topic, parentTopics, answerToShow, loading, collapsible, isPinned, revealCorrect, onSend, onAnswer, onChangeAnswer}) => {

	const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('md'))
	const theme = useTheme()
	const custom = theme.custom
	const [expanded, setExpanded] = useState(!collapsible)

	const style = {
		questionContainer: {
			// no card on either breakpoint — the question sits directly on the page
			backgroundColor: 'transparent',
			px: isSmallScreen ? 2 : 4 ,
			pt: 2,
			pb: 2,
			border: 'none',
			boxShadow: 'none'
		},
		question: {
			fontSize: isSmallScreen ? "18px" : "24px",
			lineHeight: isSmallScreen ? "22px" : "28px",
		},
		topic: {
			fontWeight: 300,
			textTransform: "uppercase",
			padding: '10px 0 10px 0',
			color: theme.palette.secondary.main
		},
		options: {
			padding: isSmallScreen
				?  isPinned ? 0 : "10px 0 10px 0"
				: "0 0 10px 0",
			marginRight: isSmallScreen ? "-10px" : 0,
			border: "0px solid red"
		},
		option: {
			padding: isSmallScreen ? "3px 0 3px 0" : "7px 0 7px 0"
		},
		optionLetter: {
			display: "inline-block",
			width: isSmallScreen     ? "18px" : "34px",
			height: isSmallScreen    ? "18px" : "34px",
			minWidth: isSmallScreen  ? "18px" : "34px",
			minHeight: isSmallScreen ? "18px" : "34px",
			textAlign: isSmallScreen ? "left" : "center",
			borderRadius: isSmallScreen ? 0 : "25px",
			color: isSmallScreen ? theme.palette.primary.main : theme.palette.text.primary,
			fontSize: isSmallScreen ? "16px" : "18px",
			fontWeight: isSmallScreen ? "500" : "inherit",
			border: isSmallScreen ? "none" : "1px solid" + theme.palette.primary.main,
			padding: isSmallScreen ? "1px 0 0 0" : "3px 4px 4px 4px",
			marginLeft: isSmallScreen ? "-5px" : 0,
			marginTop: isSmallScreen ? "2px" : "3px",
			'&:hover': {
				backgroundColor: theme.palette.primary.main,
				color: theme.palette.primary.contrastText,
			},
			transition: 'background 250ms, borderColor 250ms, color 250ms'
		},
		optionLetterCorrect: {
			borderColor: theme.palette.success.main,
			backgroundColor: isSmallScreen ? "transparent" : theme.palette.success.main,
			color: isSmallScreen ? theme.palette.success.main : theme.palette.success.contrastText
		},
		optionLetterIncorrect: {
			borderColor: theme.palette.error.main,
			backgroundColor: isSmallScreen ? "transparent" : theme.palette.error.main,
			color: isSmallScreen ? theme.palette.error.main : theme.palette.error.contrastText
		},
		optionLabel: {
			flex: 1,
			display: "inline-block",
			fontSize: isSmallScreen ? "16px" : "18px",
			marginLeft: isSmallScreen ? "8px" : "15px",
			paddingTop: isSmallScreen ? "3px" : "7px",
			paddingBottom: isSmallScreen ? "0px" : "5px",
			transition: 'color 250ms',
			border: '0px solid red'
		},
		optionLabelCorrect: {
			color: theme.palette.success.main
		},
		optionLabelIncorrect: {
			color: theme.palette.error.main,
		},
	}

	if (!question) {
		return null
	}

	return (
		<Box
			id={"tour-question"}
			sx={[
				style.questionContainer,
				{
					opacity: loading ? .2 : 1,
					transition: 'opacity 250ms',
				}
			]}
		>
			<Box
				display="flex"
				flexDirection={"row"}
				justifyContent="space-between"
				alignItems="flex-start"
				onClick={() => collapsible && setExpanded(!expanded)}
				sx={{
					cursor: collapsible ? "pointer" : "default",
					// plain text block — no background, and so no shadow to cast
					// no horizontal padding — the question text sits flush with the column
					py: isSmallScreen ? 2 : 3,
					px: 0,
					// smaller on phones: 27px would only be a couple of words per line there
				fontSize: isSmallScreen ? '22px' : '27px',
					lineHeight: 1.4,
					color: theme.palette.mode === 'dark' ? '#E8EAF6' : custom.text.main,
					// the card is a fixed width per breakpoint — long text wraps, it never widens it
					minWidth: 0,
					overflowWrap: 'anywhere',
				}}
			>
				{question.question}
				{
					collapsible &&
					<Button size="small">
						{
							expanded
								? "Less"
								: "More"
						}
						{
							expanded
								? <ExpandLess/>
								: <ExpandMore/>
						}
					</Button>
				}
			</Box>
			<Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2}}>
				{question.options.map((o, i) => {
						const answered = answerToShow && answerToShow.answer === i
						const correct = answered && answerToShow.is_correct
						/*
						 * Answers lock within a session once submitted (101). The one way back is
						 * Change Answer below, which clears answerToShow upstream — so there is no
						 * separate `unlocked` flag to keep in sync here: no answer on screen means
						 * the options are live.
						 */
						const locked = Boolean(answerToShow)
						// show green on the chosen-correct option, or on the correct option once revealed
						const showCorrect = (answered && correct) || (revealCorrect && i === question.answer)
						const showIncorrect = answered && !correct

						/*
						 * Collapsed, only the answered option shows -- but the revealed correct one has
						 * to survive that, or a wrong answer collapses to a single red option and the
						 * reveal has nothing left to render.
						 */
						if (!expanded && !answered && !showCorrect) {
							return null
						}

						const stateColor = showCorrect ? custom.status.green : showIncorrect ? custom.status.danger : null
						const isDark = theme.palette.mode === 'dark'
						// the option is a card ON the pane: raised is lighter than the pane in dark
						const cardBg = showCorrect ? `${custom.status.green}22` : showIncorrect ? `${custom.status.danger}22` : custom.bg.raised
						// border.color barely registers against the dark pane
						const cardBorder = stateColor || (isDark ? custom.border.strong : custom.border.color)
						// dim the options you didn't pick once locked
						const dim = locked && !answered && !showCorrect

						/*
						 * Only the showcased option (the answer given, or the correct one once
						 * revealed) lifts off the page. Two layers — a close 1-2px edge for
						 * definition and a wide diffuse pool for the lift. Unanswered options stay
						 * flat; the border and background carry hover.
						 */
						const shadowTint = isDark ? '0, 0, 0' : '15, 23, 42'
						// phones show the cards edge to edge, where a big shadow reads as heavy —
						// keep the lift, just less of it
						const restShadow = isSmallScreen
							? (isDark
								? `0 1px 2px rgba(${shadowTint}, 0.35), 0 6px 14px -6px rgba(${shadowTint}, 0.4)`
								: `0 1px 2px rgba(${shadowTint}, 0.03), 0 7px 16px -8px rgba(${shadowTint}, 0.10)`)
							: (isDark
								? `0 1px 2px rgba(${shadowTint}, 0.4), 0 12px 28px -8px rgba(${shadowTint}, 0.55)`
								: `0 1px 2px rgba(${shadowTint}, 0.04), 0 14px 32px -10px rgba(${shadowTint}, 0.16)`)

						return (
							<Box
								key={`option-${i}`}
								data-ga="answer-option"
								data-ga-value={abcd(i)}
								onClick={() => { if (!locked) onAnswer(question, i, o.label) }}
								sx={{
									display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
									p: isSmallScreen ? 1.5 : 2,
									borderRadius: `${custom.radius.lg}px`,
									background: cardBg,
									border: `2px solid ${cardBorder}`,
									boxShadow: (showCorrect || showIncorrect) ? restShadow : 'none',
									opacity: dim ? 0.5 : 1,
									cursor: locked ? 'default' : 'pointer',
									transition: 'background 150ms, border-color 150ms, opacity 150ms',
								'&:hover': locked ? undefined : {
										borderColor: custom.indigo.border,
										background: custom.bg.raisedHover,
									},
								}}
							>
								<Box display="flex" alignItems="center" gap={isSmallScreen ? 1.5 : 2} sx={{minWidth: 0}}>
									<Box sx={{
										width: 32, height: 32, minWidth: 32,
										display: 'flex', alignItems: 'center', justifyContent: 'center',
										borderRadius: `${custom.radius.md}px`,
										fontSize: custom.fontSize.sm, fontWeight: custom.fontWeight.bold,
										background: stateColor || custom.chip.bg,
									color: stateColor ? '#fff' : custom.chip.text,
										border: `1px solid ${cardBorder}`,
										flexShrink: 0,
									}}>
										{abcd(i)}
									</Box>
									<Box component="span" sx={{
										fontSize: '17px',
										color: stateColor || custom.text.main,
										minWidth: 0,
										overflowWrap: 'anywhere',
									}}>
										{o.label}
									</Box>
								</Box>
								</Box>
						)
					}
				)}
			</Box>

			{/*
			  * Change Answer (101). Shown only under an answer that is wrong, and only when the
			  * caller can act on it. Answering again re-completes the topic session, so the next
			  * Next lands back on the Topic Summary.
			  */}
			{answerToShow && !answerToShow.is_correct && onChangeAnswer && (
				<Box
					data-ga="change-answer"
					onClick={onChangeAnswer}
					sx={{
						display: 'inline-flex',
						alignItems: 'center',
						gap: 0.5,
						mt: 1.5,
						cursor: 'pointer',
						fontSize: custom.fontSize.sm,
						fontWeight: custom.fontWeight.semibold,
						color: custom.link,
						'&:hover': {textDecoration: 'underline'},
					}}
				>
					<LockOpen sx={{fontSize: 16}}/>
					<Box component="span">Change Answer</Box>
				</Box>
			)}

			<QuestionTranslator onTranslate={(languageName) => {
				onSend(`Show this question in ${languageName}`)
			}}/>

		</Box>)
}

export default Question